// @ts-nocheck
// PaywallStory — a narrativa que continua abaixo do CTA do paywall.
//
// É uma mini-landing dentro do app: diagnostica o problema real do leitor,
// apresenta o Protocolo, mostra a jornada, mata a objeção do fracasso e só
// então volta a pedir a assinatura.
//
// REGRA DE HONESTIDADE (não afrouxar): nada de depoimento inventado, nada de
// escassez falsa, nada de prometer resultado NO dia 67. Todo número citado tem
// fonte no `Protocolo-Habito-da-Leitura.pdf` (25/07/2026). A força de conversão
// vem do diagnóstico certo e do perdão desenhado — não de pressão.
import React from 'react';
import { Icon } from './icons';

const EMBER = '#E87A3B';
const PAPER = '#F5F1EA';
const ink = (o: number) => `rgba(245,241,234,${o})`;

const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mono" style={{
    fontSize: 9.5, letterSpacing: '0.2em', textTransform: 'uppercase',
    color: EMBER, fontWeight: 600, marginBottom: 10,
  }}>{children}</div>
);

const H: React.FC<{ children: React.ReactNode; size?: number }> = ({ children, size = 27 }) => (
  <div className="serif" style={{
    fontSize: size, lineHeight: 1.14, letterSpacing: '-0.015em', color: PAPER,
  }}>{children}</div>
);

const P: React.FC<{ children: React.ReactNode; dim?: number }> = ({ children, dim = 0.72 }) => (
  <div style={{
    fontSize: 14.5, lineHeight: 1.62, color: ink(dim), marginTop: 12,
  }}>{children}</div>
);

const Em: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{ fontStyle: 'italic', color: EMBER }}>{children}</span>
);

const Section: React.FC<{ children: React.ReactNode; top?: number }> = ({ children, top = 44 }) => (
  <div style={{ padding: `${top}px 24px 0` }}>{children}</div>
);

// Régua fina entre atos da narrativa.
const Rule: React.FC = () => (
  <div style={{ padding: '38px 24px 0' }}>
    <div style={{ height: 1, background: 'linear-gradient(90deg, rgba(232,122,59,0.35), transparent)' }}/>
  </div>
);

// Um estágio da jornada.
const Stage: React.FC<{ n: string; title: string; body: string; last?: boolean }> = ({ n, title, body, last }) => (
  <div style={{ display: 'flex', gap: 14, position: 'relative' }}>
    <div style={{ flexShrink: 0, width: 30, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{
        width: 30, height: 30, borderRadius: '50%',
        border: `1.5px solid ${EMBER}`, background: 'rgba(232,122,59,0.10)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'Geist Mono, monospace', fontSize: 11.5, color: EMBER, fontWeight: 600,
      }}>{n}</div>
      {!last && <div style={{ flex: 1, width: 1, background: 'rgba(232,122,59,0.22)', marginTop: 4 }}/>}
    </div>
    <div style={{ paddingBottom: last ? 0 : 24 }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: PAPER, marginTop: 4 }}>{title}</div>
      <div style={{ fontSize: 13.5, lineHeight: 1.55, color: ink(0.62), marginTop: 5 }}>{body}</div>
    </div>
  </div>
);

// Bloco de evidência — número grande + o que ele significa + fonte.
const Evidence: React.FC<{ stat: string; claim: string; source: string }> = ({ stat, claim, source }) => (
  <div style={{
    padding: '15px 16px', borderRadius: 14,
    background: 'rgba(245,241,234,0.04)', border: '1px solid rgba(245,241,234,0.08)',
  }}>
    <div className="serif" style={{ fontSize: 21, lineHeight: 1.1, color: EMBER }}>{stat}</div>
    <div style={{ fontSize: 13.5, lineHeight: 1.5, color: ink(0.8), marginTop: 7 }}>{claim}</div>
    <div className="mono" style={{ fontSize: 9.5, letterSpacing: '0.06em', color: ink(0.38), marginTop: 8 }}>{source}</div>
  </div>
);

export const PaywallStory: React.FC<{
  onCta: () => void;
  ctaLabel?: string;
  priceHint?: string;
}> = ({ onCta, ctaLabel = 'Assinar o acesso completo', priceHint }) => {
  return (
    <div>
      {/* ─── ATO 1 · O DIAGNÓSTICO ─────────────────────────────────── */}
      <Rule/>
      <Section>
        <Eyebrow>Antes de tudo</Eyebrow>
        <H>Você não tem<br/><Em>falta de tempo</Em>.</H>
        <P>
          Essa é a explicação que todo mundo dá — e é a errada. Quando pesquisadores
          olharam o que as pessoas realmente fazem com as horas do dia, acharam o
          contrário do esperado: quem lê tem a agenda <strong style={{ color: PAPER }}>mais cheia</strong>,
          não mais vazia. E quem senta pra ler hoje lê por mais tempo do que se lia
          antes.
        </P>
        <P>
          O que despencou não foi a duração. Foi a <Em>entrada</Em> — o ato de abrir
          o livro. A leitura não morreu de cansaço no meio do capítulo; ela morreu
          na porta, no instante entre pegar o livro e pegar o celular.
        </P>
      </Section>

      <Section top={26}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Evidence
            stat="7,6 × 5,3"
            claim="Quem lê cita 7,6 atividades no tempo livre; quem não lê, 5,3. A única em que o não-leitor ganha é a TV. Leitura não é sobra de tempo — é escolha de entrada."
            source="Retratos da Leitura no Brasil, 6ª ed. · Ipec, 5.504 entrevistas"
          />
          <Evidence
            stat="1h23 → 1h37"
            claim="A sessão de leitura AUMENTOU. O que caiu foi quantas pessoas começam: de 28% para 16% num dia comum."
            source="Bone et al., 2025 · iScience · N = 236.270"
          />
        </div>
        <P dim={0.62}>
          É por isso que resumo, leitura dinâmica e “ler 2× mais rápido” não
          resolvem nada. Eles atacam a duração. O seu problema é a porta.
        </P>
      </Section>

      {/* ─── ATO 2 · O INIMIGO ─────────────────────────────────────── */}
      <Rule/>
      <Section>
        <Eyebrow>O que está no seu caminho</Eyebrow>
        <H>Não é preguiça.<br/>É <Em>arquitetura</Em>.</H>
        <P>
          A pilha de livros comprados e não terminados não é um defeito de caráter.
          É o resultado previsível de um dia inteiro projetado por outras pessoas pra
          capturar sua atenção — contra um livro que não faz barulho, não vibra e não
          te procura.
        </P>
        <P>
          Força de vontade é um recurso que acaba todo dia por volta das nove da
          noite. Quem lê muito não tem mais força de vontade que você.
          Tem <strong style={{ color: PAPER }}>menos decisões a tomar</strong>: sabe
          qual livro, sabe a hora, sabe onde senta. A leitura já está decidida antes
          de a noite começar.
        </P>
        <P>
          O Forjamente existe pra tomar essas decisões por você — e devolver o livro
          pro lugar onde ele consegue competir.
        </P>
      </Section>

      {/* ─── ATO 3 · O PROTOCOLO ───────────────────────────────────── */}
      <Rule/>
      <Section>
        <Eyebrow>O que você recebe</Eyebrow>
        <H>Um protocolo de<br/><Em>67 dias</Em>. Não um app de metas.</H>
        <P>
          A maioria dos aplicativos te dá um contador e deseja boa sorte. O
          Forjamente te conduz por um programa desenhado a partir do que a pesquisa
          sobre formação de hábito realmente mostrou — inclusive quando isso
          contraria o que se vende por aí.
        </P>
        <P>
          Você não começa escolhendo metas. Você começa respondendo poucas perguntas,
          escolhendo entre <strong style={{ color: PAPER }}>duas ou três capas</strong> que
          fazem sentido pra você, e assinando um contrato de leitura com duas
          informações apenas: <Em>quando</Em> e <Em>onde</Em>. Só isso. Contratos
          maiores funcionam pior — está medido.
        </P>
      </Section>

      <Section top={30}>
        <div className="mono" style={{
          fontSize: 9.5, letterSpacing: '0.2em', textTransform: 'uppercase',
          color: ink(0.45), marginBottom: 18,
        }}>A jornada, estágio por estágio</div>
        <Stage n="0" title="Qualificação e curadoria"
          body="O app descobre que tipo de leitor você é e monta uma escolha curta e certeira. Sem catálogo infinito, sem paralisia."/>
        <Stage n="1" title="O contrato"
          body="Quando e onde você vai ler. Você ensaia uma vez — e ensaiar uma vez muda o resultado de forma mensurável."/>
        <Stage n="2" title="Ignição"
          body="Os primeiros dias com zero decisões: a Home abre o livro certo, no ponto certo. Ler vira um toque."/>
        <Stage n="3" title="O Vale"
          body="O estágio que os outros apps fingem que não existe: o dia em que você falha. Aqui ele é previsto, acolhido e transformado."/>
        <Stage n="4" title="Transferência"
          body="Os lembretes começam a se aposentar de propósito. O gatilho sai do celular e passa pro mundo físico — a poltrona, o café, a cabeceira."/>
        <Stage n="5" title="Consolidação"
          body="A automaticidade é medida de verdade, com um instrumento validado. Você vê o hábito se instalando em números honestos."/>
        <Stage n="6" title="Identidade"
          body="Deixa de ser algo que você está tentando fazer e vira algo que você é. Aqui o protocolo se despede — de propósito." last/>
      </Section>

      {/* ─── ATO 4 · A OBJEÇÃO (O VALE) ────────────────────────────── */}
      <Rule/>
      <Section>
        <Eyebrow>A pergunta que importa</Eyebrow>
        <H>“E quando eu<br/><Em>falhar</Em>?”</H>
        <P>
          Você vai falhar. Todo mundo falha — e é exatamente aqui que os aplicativos
          de sequência te perdem: eles zeram seu contador, você sente vergonha, e
          nunca mais abre.
        </P>
        <P>
          O maior experimento já feito sobre criação de hábitos testou 53 estratégias
          diferentes ao mesmo tempo, com mais de 61 mil pessoas. A vencedora não foi
          punir a falta. Foi <strong style={{ color: PAPER }}>recompensar o retorno</strong> —
          dar algo bom pra quem volta depois de faltar.
        </P>
        <P>
          Então o Forjamente premia sua volta. Um dia perdido não apaga seu progresso
          (e, na medida real, quase não custa nada). Existe um roteiro pra quando você
          sumir, e existe permissão explícita pra abandonar um livro sem quebrar nada.
          O protocolo foi desenhado pra <Em>te trazer de volta</Em>, não pra te
          fiscalizar.
        </P>
      </Section>

      <Section top={26}>
        <Evidence
          stat="61.293 pessoas"
          claim="Num experimento com 53 intervenções em paralelo, a estratégia campeã foi a micro-recompensa por voltar depois de uma falta."
          source="Milkman et al., 2021 · Nature"
        />
      </Section>

      {/* ─── ATO 5 · POR QUE FUNCIONA ─────────────────────────────── */}
      <Rule/>
      <Section>
        <Eyebrow>Por que isso funciona</Eyebrow>
        <H>Cada detalhe aqui<br/>foi <Em>medido</Em>.</H>
        <P>
          O Forjamente foi construído em cima de um documento de pesquisa próprio, que
          revisou mais de 90 fontes — e que jogou fora tudo que era lenda de internet.
          Os 21 dias que todo mundo cita? Vieram de um livro de cirurgia plástica de
          1960 e nunca foram sobre hábitos. Aqui você não vai encontrar isso.
        </P>
      </Section>

      <Section top={22}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Evidence
            stat="+10 minutos por dia"
            claim="Dez minutos diários de leitura movem um leitor do percentil 30 pra além do percentil 70 em volume lido no ano. É o pouco que muda a escala."
            source="Anderson, Wilson & Fielding, 1988"
          />
          <Evidence
            stat="Lembrete tem validade"
            claim="Notificação eterna impede o hábito de se formar — a pessoa vira dependente do aviso. As do Forjamente decaem de propósito, e o gatilho migra pro seu ambiente."
            source="Stawarz et al. · via Wood & Rünger"
          />
          <Evidence
            stat="Progresso, não promessa"
            claim="Anunciar publicamente a intenção de ler REDUZ a execução; relatar progresso com apoio aumenta. As telas sociais do app foram auditadas por essa régua."
            source="Gollwitzer et al., 2009 · Peetz, 2025"
          />
        </div>
      </Section>

      {/* ─── ATO 6 · O QUE MUDA NO DIA ─────────────────────────────── */}
      <Rule/>
      <Section>
        <Eyebrow>No dia a dia</Eyebrow>
        <H>O que você<br/>vai <Em>encontrar</Em>.</H>
        <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 15 }}>
          {[
            ['Uma Home que já sabe o que fazer', 'Abre no livro certo, na página certa. Sem menu, sem escolha, sem desculpa.'],
            ['A chama e a sequência', 'O registro que mostra o hábito ganhando corpo — e que perdoa, em vez de punir.'],
            ['O Protocolo do Hábito', 'O programa completo de 67 dias, com todos os estágios, marcos e a medição real de automaticidade.'],
            ['Clubes de leitura', 'Ler junto de quem te puxa pra cima: entre em quantos quiser, crie os seus, conduza o grupo.'],
            ['Ferramentas de líder', 'Se você conduz um grupo: quem leu, em que ritmo, marcos de leitura e caixa de perguntas.'],
            ['Sua estante viva', 'Todo livro que você leu, está lendo e vai ler — com a sua história de leitura junto.'],
          ].map(([t, b], i) => (
            <div key={i} style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
              <div style={{ marginTop: 3, flexShrink: 0 }}>
                <Icon name="check" size={13} color={EMBER} stroke={2.6}/>
              </div>
              <div>
                <div style={{ fontSize: 14.5, fontWeight: 600, color: PAPER }}>{t}</div>
                <div style={{ fontSize: 13, lineHeight: 1.5, color: ink(0.6), marginTop: 3 }}>{b}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ─── ATO 7 · A CONTA ──────────────────────────────────────── */}
      <Rule/>
      <Section>
        <Eyebrow>A conta</Eyebrow>
        <H>Menos de <Em>meio livro</Em><br/>por um ano inteiro.</H>
        <P>
          O livro médio no varejo brasileiro custa cerca de R$ 57. O acesso completo
          ao Forjamente por um ano inteiro custa menos que a metade de um — e é o que
          decide se os outros vão ser <strong style={{ color: PAPER }}>lidos ou empilhados</strong>.
        </P>
        <P dim={0.62}>
          Você já gastou mais do que isso em livros que não terminou. Essa é a parte
          que faltava.
        </P>
      </Section>

      {/* ─── ATO 8 · A PROMESSA HONESTA ───────────────────────────── */}
      <Rule/>
      <Section>
        <Eyebrow>O que não vamos prometer</Eyebrow>
        <H>Nenhum app vira seu<br/>hábito no <Em>dia 67</Em>.</H>
        <P>
          Quatro estudos independentes convergem numa mediana entre 59 e 78 dias pra
          um comportamento virar automático — por isso os 67. Mas a faixa real vai de
          18 a 254 dias, e uma parte das pessoas do estudo original simplesmente não
          formou o hábito.
        </P>
        <P>
          Preferimos te dizer isso agora a te vender um número bonito. O que o
          protocolo faz é te dar a melhor estrutura conhecida, medir de verdade onde
          você está, e — principalmente — <Em>continuar te trazendo de volta</Em> nos
          dias em que você não quiser voltar.
        </P>
      </Section>

      {/* ─── CTA FINAL ────────────────────────────────────────────── */}
      <Section top={40}>
        <div style={{
          padding: '26px 22px',
          borderRadius: 20,
          background: 'linear-gradient(135deg, rgba(199,83,26,0.20), rgba(232,122,59,0.06))',
          border: '1px solid rgba(232,122,59,0.35)',
          textAlign: 'center',
        }}>
          <H size={24}>Seu próximo livro<br/>pode ser <Em>o que ficou</Em>.</H>
          <div style={{ fontSize: 14, lineHeight: 1.55, color: ink(0.72), marginTop: 12 }}>
            Acesso completo ao app e ao Protocolo do Hábito, desde o primeiro dia.
          </div>
          <button onClick={onCta} style={{
            width: '100%', marginTop: 20, padding: '16px 24px',
            background: EMBER, color: PAPER, border: 'none', borderRadius: 999,
            fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
            boxShadow: '0 10px 26px -8px rgba(199,83,26,0.6)',
          }}>
            {ctaLabel}
            <Icon name="arrow-right" size={15} color={PAPER} stroke={2.2}/>
          </button>
          {priceHint && (
            <div style={{ fontSize: 11.5, color: ink(0.5), marginTop: 10 }}>{priceHint}</div>
          )}
        </div>
      </Section>
    </div>
  );
};
